import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { ChangedFile } from '../../types';
import { antGrey, runwayColors, titleStyle } from '../styles/globalStyles';
import { ColumnType } from 'antd/lib/table';
import Table from 'antd/lib/table/Table';
import { InfoCircleOutlined } from '@ant-design/icons';
import Divider from 'antd/lib/divider';
import Tabs from 'antd/lib/tabs';
import Tooltip from 'antd/lib/tooltip';
import Space from 'antd/lib/space';

interface FilesChangedProps {
    topChangedFiles: ChangedFile[];
    totalFilesChanged: number;
}

const FilesChanged: React.FC<FilesChangedProps> = (props: FilesChangedProps) => {
    const { topChangedFiles, totalFilesChanged } = props;
    const [activeTabKey, setActiveTabKey] = React.useState('top');

    const topRelevantFiles = topChangedFiles.filter((file) => !file.isConfigFile);

    const tabPaneTextStyle: React.CSSProperties = {
        color: runwayColors.primary,
        textAlign: 'center',
    };

    const tabPanelTextContainerStyle: React.CSSProperties = {
        paddingLeft: 12,
        paddingRight: 12,
        display: 'flex',
        justifyContent: 'center',
    };

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                height: '230px',
                borderRadius: '8px',
                backgroundColor: antGrey.grey2,
                color: antGrey.grey7,
                padding: '18px',
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text style={{ color: antGrey.grey7 }}>Files Changed</Text>
                <Text style={{ color: antGrey.grey8, fontSize: '24px', fontWeight: 600 }}>{totalFilesChanged}</Text>
            </div>
            <Divider type="vertical" style={{ margin: '0px 20px', height: '10em' }} />
            <div style={{ display: 'flex', flexDirection: 'column', flex: 7 }}>
                <Tabs
                    tabBarExtraContent={{
                        left: <Text style={{ ...titleStyle, fontSize: 10, paddingRight: 16 }}>Changed Files</Text>,
                        right: <Text style={{ color: antGrey.grey7 }}>Changes</Text>,
                    }}
                    activeKey={activeTabKey}
                    style={{
                        backgroundColor: antGrey.grey2,
                        borderRadius: 8,
                        paddingTop: 0,
                        height: '100%',
                    }}
                    tabBarStyle={{ marginBottom: 10 }}
                    size="small"
                    onTabClick={(activeKey) => setActiveTabKey(activeKey)}
                    className="files-changed-tab"
                >
                    <Tabs.TabPane
                        key="top"
                        tab={
                            <div style={tabPanelTextContainerStyle}>
                                <Space align="center">
                                    <Text style={tabPaneTextStyle}>Relevant files</Text>
                                    <Tooltip title="Relevant files changed, not including configuration files.">
                                        <InfoCircleOutlined style={{ color: runwayColors.primary }} />
                                    </Tooltip>
                                </Space>
                            </div>
                        }
                    >
                        <ChangedFilesTable files={topRelevantFiles} />
                    </Tabs.TabPane>
                    <Tabs.TabPane
                        key="all"
                        tab={
                            <div style={{ ...tabPanelTextContainerStyle }}>
                                <Text style={tabPaneTextStyle}>All files</Text>
                            </div>
                        }
                    >
                        <ChangedFilesTable files={topChangedFiles} />
                    </Tabs.TabPane>
                </Tabs>
            </div>
        </div>
    );
};

const ChangedFilesTable = ({ files }: { files: ChangedFile[] }) => {
    const columns: ColumnType<ChangedFile>[] = [
        {
            dataIndex: 'fileName',
            key: 'filename',
            render: (text) => <Text code>{text}</Text>,
            width: '75%',
        },
        {
            dataIndex: 'linesChangedCount',
            key: 'linesChangedCount',
            width: 50,
            align: 'right',
        },
    ];
    return (
        <Table
            columns={columns}
            dataSource={files}
            bordered={false}
            pagination={false}
            rowClassName={'files-changed-table-row'}
            size="small"
            locale={{ emptyText: 'No changed files' }}
            style={{ margin: -8, wordBreak: 'break-word' }} //negative margin is hacky way to remove header padding: ;
            rowKey="fileName"
            onRow={() => {
                return {
                    style: { backgroundColor: antGrey.grey2 },
                };
            }}
            showHeader={false}
        ></Table>
    );
};

export default FilesChanged;
